import { useLayoutEffect, useRef, useState } from 'react'

/**
 * Measures child elements matching `selector` inside a container ref and
 * determines how many fit within the available width (with a 4px gap between
 * items). When `maxWidth` is omitted the container's own width is used, so the
 * caller can size the container via CSS and the overflow count will adapt.
 * Overflow items are hidden via direct DOM manipulation to prevent a flash,
 * and the counts are exposed for render-time use.
 */
export function useOverflowCount(
  items: unknown[],
  selector: string,
  maxWidth?: number,
  reserveWidth?: number,
  reserveAlways = false,
): {
  containerRef: React.RefObject<HTMLSpanElement>
  visibleCount: number | null
  shown: number
  overflow: number
} {
  const containerRef = useRef<HTMLSpanElement>(null)
  const [visibleCount, setVisibleCount] = useState<number | null>(null)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  const effectiveMaxWidth = maxWidth ?? containerWidth

  useLayoutEffect(() => {
    if (maxWidth !== undefined) return
    const container = containerRef.current
    if (!container) return
    const updateWidth = (): void => setContainerWidth(container.clientWidth)
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(container)
    return (): void => observer.disconnect()
  }, [maxWidth])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || effectiveMaxWidth == null || effectiveMaxWidth <= 0) {
      setVisibleCount(null)
      return
    }

    const elements = container.querySelectorAll<HTMLElement>(selector)
    if (elements.length === 0) return

    for (const el of elements) el.style.removeProperty('display')

    let total = 0
    let countWithoutReserve = 0
    for (const el of elements) {
      total += el.offsetWidth + (countWithoutReserve > 0 ? 4 : 0)
      if (total > effectiveMaxWidth) break
      countWithoutReserve++
    }

    let count = countWithoutReserve
    const needsReserve =
      countWithoutReserve < items.length ||
      (reserveAlways && total + (reserveWidth ?? 0) > effectiveMaxWidth)
    if (needsReserve && reserveWidth && reserveWidth > 0) {
      const widthForItems = Math.max(0, effectiveMaxWidth - reserveWidth)
      total = 0
      count = 0
      for (const el of elements) {
        total += el.offsetWidth + (count > 0 ? 4 : 0)
        if (total > widthForItems) break
        count++
      }
    }
    count = Math.max(1, count)

    const next = count < items.length ? count : null

    for (let i = 0; i < elements.length; i++) {
      if (next !== null && i >= next) elements[i].style.display = 'none'
    }

    setVisibleCount(next)
  }, [items, selector, effectiveMaxWidth, reserveWidth, reserveAlways])

  const shown = visibleCount ?? items.length
  const overflow = items.length - shown

  return { containerRef, visibleCount, shown, overflow }
}
